import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Divider,
  FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, MenuItem,
  Paper, Select, Skeleton, Snackbar,
  Accordion, AccordionDetails, AccordionSummary,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  TextField, Tooltip, Typography,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Grid,
} from '@mui/material';
import {
  Add, ArrowBack, Refresh, Search, TuneRounded, Print, FileDownloadOutlined,
  Block as VoidIcon, Undo as RefundIcon, Visibility, Save, ExpandMore,
  Close as CloseIcon, ReceiptOutlined, PersonOutlined,
  CheckCircleOutlined, WarningAmberOutlined, TaskAlt,
  ShareOutlined as ShareIcon, LinkOutlined as LinkIcon,
  LinkOff as CancelLinkIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../../../contexts/PermissionContext';
import * as api from '../../../../services/subscriptionAPI';

// ── Invoice document brand palette (matches glammify-logo.png) ───────────────
// Logo letters   : black #111111
// Gradient accent: purple #6117C6 → pink #F66CAC → gold #F2CC00
const INV = {
  purple:   '#6117C6',   // gradient start
  pink:     '#F66CAC',   // gradient mid
  gold:     '#F2CC00',   // gradient end
  charcoal: '#111827',   // dark strip background (matches black letters)
  dark:     '#0B0F1A',   // darkest for footer strip
  bgLight:  '#FAF6FF',   // very light purple-tinted bg
  bgLighter:'#FDF9FF',   // table row hover
  border:   '#EDE0FF',   // light purple border
  gradient: 'linear-gradient(90deg, #6117C6 0%, #F66CAC 55%, #F2CC00 100%)',
};

// ── helpers ───────────────────────────────────────────────────────────────────
const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtAmt = (v, cur = 'INR') =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: cur, maximumFractionDigits: 2 }).format(v || 0);

// ── status config ─────────────────────────────────────────────────────────────

const STATUS_META = {
  draft:    { label: 'Draft',            bg: '#F5F5F5', color: '#757575', dot: '#9E9E9E' },
  issued:   { label: 'Awaiting Payment', bg: '#FFF8E1', color: '#F57F17', dot: '#FFA000' },
  paid:     { label: 'Paid',             bg: '#E8F5E9', color: '#2E7D32', dot: '#43A047' },
  void:     { label: 'Voided',           bg: '#FFEBEE', color: '#C62828', dot: '#E53935' },
  refunded: { label: 'Refunded',         bg: '#FFF3E0', color: '#E65100', dot: '#FB8C00' },
};

function StatusChip({ status }) {
  const c = STATUS_META[status] || STATUS_META.draft;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6,
      px: 1.2, py: 0.4, borderRadius: 1.5, bgcolor: c.bg }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c.dot, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: c.color }}>{c.label}</Typography>
    </Box>
  );
}

const SortTH = ({ label, field, sortBy, sortDir, onSort, sx = {} }) => (
  <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'pointer', userSelect: 'none', ...sx }}
    onClick={() => onSort(field)}>
    {label}
    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 0.25, opacity: sortBy === field ? 1 : 0.3 }}>
      <svg width="8" height="5" viewBox="0 0 8 5"><path d="M4 0L8 5H0Z" fill={sortBy === field && sortDir === 'asc' ? 'currentColor' : '#9ca3af'}/></svg>
      <svg width="8" height="5" viewBox="0 0 8 5"><path d="M4 5L0 0H8Z" fill={sortBy === field && sortDir === 'desc' ? 'currentColor' : '#9ca3af'}/></svg>
    </Box>
  </Box>
);

const LIST_TH = {
  fontWeight: 700, fontSize: 11, color: 'text.secondary',
  letterSpacing: 0.6, textTransform: 'uppercase', bgcolor: 'grey.50', py: 1.5,
};
const _BTN     = { width: 30, height: 30, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' };
const BTN_PRIMARY = { ..._BTN, color: 'primary.main', '&:hover': { bgcolor: 'primary.50',  borderColor: 'primary.200'  } };
const BTN_SUCCESS = { ..._BTN, color: 'success.main', '&:hover': { bgcolor: 'success.50',  borderColor: 'success.200'  } };
const BTN_ERROR   = { ..._BTN, color: 'error.main',   '&:hover': { bgcolor: 'error.50',    borderColor: 'error.200'    } };
const BTN_WARNING = { ..._BTN, color: 'warning.dark', '&:hover': { bgcolor: 'warning.50',  borderColor: 'warning.200'  } };

const DOC_TH = {
  fontWeight: 700, fontSize: 10.5, color: '#78909C',
  letterSpacing: 0.8, textTransform: 'uppercase',
  bgcolor: '#F8FAFC', borderBottom: '1px solid #E8EDF2', py: 1.5,
};

const ACCORDION_SX = {
  border: '1px solid', borderColor: 'divider', borderRadius: '10px !important',
  bgcolor: 'background.paper', boxShadow: 'none', '&:before': { display: 'none' },
  '&.Mui-expanded': { borderColor: 'primary.light' },
};

function LabelValue({ label, value, mono }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#90A4AE', letterSpacing: 0.8,
        textTransform: 'uppercase', mb: 0.3 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1A2027',
        fontFamily: mono ? "'Roboto Mono', monospace" : 'inherit' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

// ── GST compute ───────────────────────────────────────────────────────────────
function computeGST(pricingRow, taxRules) {
  const taxable = parseFloat(pricingRow.mrp_excl_tax) || 0;
  const grand   = parseFloat(pricingRow.mrp_incl_tax) || taxable;
  if (!pricingRow.tax_rule_id || taxable === grand)
    return { taxable, cgst: 0, sgst: 0, igst: 0, cess: 0, total_tax: 0, grand };
  const rule = taxRules.find(r => r.id === pricingRow.tax_rule_id);
  if (!rule) return { taxable, cgst: 0, sgst: 0, igst: 0, cess: 0, total_tax: grand - taxable, grand };
  let cgst = 0, sgst = 0, igst = 0, cess = 0;
  (rule.components || []).forEach(c => {
    const amt = (taxable * parseFloat(c.rate_percent)) / 100;
    const t = c.component_type?.toLowerCase();
    if (t === 'cgst') cgst += amt;
    else if (t === 'sgst') sgst += amt;
    else if (t === 'igst') igst += amt;
    else if (t === 'cess') cess += amt;
  });
  return { taxable, cgst, sgst, igst, cess,
    total_tax: parseFloat((cgst + sgst + igst + cess).toFixed(2)), grand };
}

const EMPTY_PAY = {
  subscription_id: '', currency_code: '', billing_period: '',
  amount: '', gateway: 'manual', status: 'captured',
  payment_mode: '', payment_date: '', admin_notes: '',
};

const NUM_NO_SPIN_INV = {
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
    { WebkitAppearance: 'none', margin: 0 },
  '& input[type=number]': { MozAppearance: 'textfield' },
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════════
export default function InvoicesManagement() {
  const { hasPermission } = usePermissions();
  const canView    = hasPermission('Invoices', 'view');
  const canVoid    = hasPermission('Invoices', 'void');
  const canRefund  = hasPermission('Invoices', 'refund');
  const canIssue   = hasPermission('Invoices', 'issue');
  const canPay     = hasPermission('Subscriptions', 'record_manual_payment');

  const [view, setView] = useState('list');

  const [items, setItems]             = useState([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(0);
  const [perPage, setPerPage]         = useState(10);
  const [sortBy, setSortBy]           = useState('invoice_number');
  const [sortDir, setSortDir]         = useState('desc');
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch]               = useState('');
  const [statusFilter, setStatus]         = useState('');
  const [payStatusFilter, setPayStatus]   = useState('');

  const [payForm, setPayForm]             = useState(EMPTY_PAY);
  const [subs, setSubs]                   = useState([]);
  const [planVersions, setPlanVers]       = useState([]);
  const [taxRules, setTaxRules]           = useState([]);
  const [masterCurrencies, setMasterCurrencies] = useState([]);
  const [gst, setGst]               = useState(null);
  const [saving, setSaving]         = useState(false);

  const [invoice, setInvoice]             = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refundOpen, setRefundOpen]       = useState(false);
  const [refundForm, setRefundForm]       = useState({ refund_amount: '', reason: '', refund_reference: '' });
  const [refunding, setRefunding]         = useState(false);
  const [payErrors, setPayErrors]         = useState({});
  const [refundErrors, setRefundErrors]   = useState({});
  const [voidOpen, setVoidOpen]           = useState(false);
  const [voidReason, setVoidReason]       = useState('');
  const [voiding, setVoiding]             = useState(false);
  const [linkSending, setLinkSending]     = useState(false);
  const [linkResult, setLinkResult]       = useState(null); // { sent, email, payment_url, message }
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkEmailInput, setLinkEmailInput] = useState('');
  const [linkEmailError, setLinkEmailError] = useState('');

  // ── Mark as Received ──────────────────────────────────────────────────────
  const [markOpen, setMarkOpen]           = useState(false);
  const [markTarget, setMarkTarget]       = useState(null);  // {id, invoice_ref}
  const [marking, setMarking]             = useState(false);
  const [markForm, setMarkForm]           = useState({ payment_date: '', reference_number: '', notes: '' });

  // ── Coupon (Record Payment form) ───────────────────────────────────────────
  const [couponInput, setCouponInput]         = useState('');
  const [couponPreview, setCouponPreview]     = useState(null);
  const [couponError, setCouponError]         = useState('');
  const [couponApplying, setCouponApplying]   = useState(false);
  const [originalAmount, setOriginalAmount]   = useState(null); // before coupon discount

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const show = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  // ── list ──────────────────────────────────────────────────────────────────
  const loadItems = useCallback(async () => {
    setListLoading(true);
    try {
      const r = await api.getInvoices({
        search:         search          || undefined,
        status:         statusFilter    || undefined,
        payment_status: payStatusFilter || undefined,
        page: page + 1,
        per_page: perPage,
        sort_by:  sortBy,
        sort_dir: sortDir,
      });
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    } catch { show('Failed to load invoices', 'error'); }
    finally { setListLoading(false); }
  }, [search, statusFilter, payStatusFilter, page, perPage, sortBy, sortDir]);

  useEffect(() => { setPage(0); }, [search, statusFilter, payStatusFilter, sortBy, sortDir]);
  useEffect(() => { if (view === 'list') loadItems(); }, [view, loadItems]);

  const removeCoupon = () => {
    if (originalAmount !== null) {
      setPayForm(f => ({ ...f, amount: String(originalAmount) }));
      setOriginalAmount(null);
    }
    setCouponInput(''); setCouponPreview(null); setCouponError('');
  };

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    if (!payForm.subscription_id) { setCouponError('Select a subscription first'); return; }
    if (!payForm.billing_period)  { setCouponError('Select a billing period first'); return; }
    setCouponApplying(true); setCouponError('');
    try {
      const sub = subs.find(s => s.id === Number(payForm.subscription_id));
      const r = await api.previewCoupon({
        code,
        user_id:         sub?.user_id,
        plan_version_id: sub?.plan_version_id,
        billing_period:  payForm.billing_period,
      });
      const preview = r.data;
      setCouponPreview(preview);
      // Save original amount and apply discounted price
      const orig = parseFloat(payForm.amount) || 0;
      setOriginalAmount(orig);
      if (preview.discounted_price != null) {
        setPayForm(f => ({ ...f, amount: String(preview.discounted_price) }));
      }
    } catch (e) {
      setCouponError(e.response?.data?.message || 'Invalid coupon');
      setCouponPreview(null);
    } finally { setCouponApplying(false); }
  };

  const handleExport = async () => {
    try {
      const res = await api.exportInvoices({
        status: statusFilter || undefined,
        payment_status: payStatusFilter || undefined,
        search: search || undefined,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { show('Export failed', 'error'); }
  };

  const openRecord = async () => {
    setPayForm(EMPTY_PAY); setGst(null);
    setCouponInput(''); setCouponPreview(null); setCouponError(''); setOriginalAmount(null);
    try {
      const [subsR, pvR, trR, curR] = await Promise.all([
        api.getSubscriptions({ per_page: 100 }),
        api.getSubscriptionPlanVersions(),
        api.getTaxRules(),
        api.getCurrencies(),
      ]);
      setSubs(subsR.data.items || []);
      setPlanVers(pvR.data || []);
      setTaxRules(trR.data || []);
      setMasterCurrencies(curR.data || []);
    } catch {}
    setView('record');
  };

  const _autoFillFromPricing = (subId, allPricing, cur, period) => {
    if (!period || !allPricing.length) return;
    const row = allPricing.find(p => p.billing_period === period && (!cur || p.currency_code === cur));
    if (!row) return;
    const breakdown = computeGST(row, taxRules);
    setGst(breakdown);
    setPayForm(f => ({ ...f, subscription_id: subId, currency_code: row.currency_code,
      billing_period: period, amount: String(breakdown.grand) }));
  };

  const handleSubSelect = (subId) => {
    setPayForm(f => ({ ...f, subscription_id: subId, currency_code: '', billing_period: '', amount: '' }));
    setGst(null);
    if (!subId) return;
    const sub    = subs.find(s => s.id === Number(subId));
    const planV  = sub ? planVersions.find(pv => pv.plan_version_id === sub.plan_version_id) : null;
    const allP   = planV?.pricing || [];
    const curs   = [...new Set(allP.map(p => p.currency_code))];
    const cur    = curs.length === 1 ? curs[0] : '';
    const prds   = allP.filter(p => !cur || p.currency_code === cur).map(p => p.billing_period);
    const period = prds.length === 1 ? prds[0] : '';
    if (cur || period) _autoFillFromPricing(subId, allP, cur, period);
  };

  const handleCurrencySelect = (cur) => {
    setPayForm(f => ({ ...f, currency_code: cur, billing_period: '', amount: '' }));
    setGst(null);
  };
  const handlePeriodSelect = (period, allPricing) => {
    const row = allPricing.find(p => p.billing_period === period);
    if (!row) { setPayForm(f => ({ ...f, billing_period: period, amount: '' })); setGst(null); return; }
    const breakdown = computeGST(row, taxRules);
    setGst(breakdown);
    setPayForm(f => ({ ...f, billing_period: period, amount: String(breakdown.grand), currency_code: row.currency_code }));
  };

  const submitPayment = async () => {
    const errs = {};
    if (!payForm.subscription_id) errs.subscription_id = 'Select a subscription';
    if (!payForm.amount || Number(payForm.amount) <= 0) errs.amount = 'Enter a valid amount (> 0)';
    if (payForm.currency_code && payForm.currency_code.length !== 3) errs.currency_code = 'Use 3-letter code (e.g. INR)';
    if (Object.keys(errs).length > 0) { setPayErrors(errs); return; }
    setPayErrors({});
    setSaving(true);
    try {
      const payRes = await api.recordPayment(payForm.subscription_id, {
        amount:         Number(payForm.amount),
        currency_code:  payForm.currency_code,
        billing_period: payForm.billing_period  || undefined,
        gateway:        payForm.gateway,
        status:         payForm.status,
        payment_mode:   payForm.payment_mode    || undefined,
        payment_date:   payForm.payment_date    || undefined,
        admin_notes:    payForm.admin_notes     || undefined,
        coupon_code:    couponPreview?.code      || undefined,
      });
      try {
        const invRes = await api.issueInvoice(payRes.data.id);
        show('Payment recorded & invoice issued');
        openDetail(invRes.data.id);
      } catch (ie) {
        const msg = ie?.response?.data?.message || ie?.message || 'unknown error';
        show(`Payment recorded but invoice failed: ${msg}`, 'warning');
        setView('list');
      }
    } catch (e) { show(e.response?.data?.message || 'Failed to record payment', 'error'); }
    finally { setSaving(false); }
  };

  const openDetail = async (id) => {
    setDetailLoading(true); setInvoice(null);
    setRefundOpen(false); setVoidOpen(false); setView('detail');
    try {
      const r = await api.getInvoice(id);
      setInvoice(r.data);
      setRefundForm({ refund_amount: r.data.grand_total, reason: '', refund_reference: '' });
    } catch { show('Failed to load invoice', 'error'); setView('list'); }
    finally { setDetailLoading(false); }
  };

  const refreshDetail = async () => {
    if (!invoice) return;
    try { const r = await api.getInvoice(invoice.id); setInvoice(r.data); } catch {}
  };

  const handleOpenLinkDialog = () => {
    setLinkEmailInput(invoice?.merchant_email || '');
    setLinkEmailError('');
    setLinkResult(null);
    setLinkDialogOpen(true);
  };

  const handleSendPaymentLink = async () => {
    const email = linkEmailInput.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLinkEmailError('Enter a valid email address');
      return;
    }
    setLinkSending(true);
    setLinkDialogOpen(false);
    try {
      const res = await api.sendPaymentLink(invoice.id, { email });
      setLinkResult(res.data);
      show(res.data.message, res.data.sent ? 'success' : 'warning');
    } catch (e) {
      const msg = e.response?.data?.error || 'Failed to send payment link';
      show(msg, 'error');
    } finally {
      setLinkSending(false);
    }
  };

  const [syncingPayment, setSyncingPayment] = useState(false);
  const handleSyncPaymentStatus = async () => {
    setSyncingPayment(true);
    try {
      const res = await api.syncPaymentLinkStatus(invoice.id);
      show(res.data.message, res.data.changed ? 'success' : 'info');
      if (res.data.changed) { try { const r = await api.getInvoice(invoice.id); setInvoice(r.data); } catch {} }
    } catch (e) {
      show(e.response?.data?.message || 'Sync failed', 'error');
    } finally {
      setSyncingPayment(false);
    }
  };

  const [cancellingLink, setCancellingLink] = useState(false);
  const [cancelLinkOpen, setCancelLinkOpen] = useState(false);
  const handleCancelPaymentLink = async () => {
    setCancellingLink(true);
    setCancelLinkOpen(false);
    try {
      await api.cancelPaymentLink(invoice.id);
      show('Payment link cancelled', 'success');
      try { const r = await api.getInvoice(invoice.id); setInvoice(r.data); } catch {}
    } catch (e) {
      show(e.response?.data?.message || 'Failed to cancel payment link', 'error');
    } finally {
      setCancellingLink(false);
    }
  };

  const handlePrint = () => {
    const style = document.createElement('style');
    style.id = '__inv_print__';
    style.innerHTML = `
      @page { size: A4; margin: 0; }
      @media print {
        html, body { height: auto !important; overflow: hidden !important; }
        body * { visibility: hidden !important; }
        #invoice-print-area, #invoice-print-area * { visibility: visible !important; }
        #invoice-print-area {
          position: absolute; top: 0; left: 0;
          width: 100%; padding: 1.4cm; box-sizing: border-box;
          box-shadow: none !important; border: 1px solid #D1D5DB !important;
          border-radius: 0 !important; background: white !important;
        }
      }`;
    document.head.appendChild(style);
    window.print();
    document.head.removeChild(style);
  };

  const submitVoid = async (targetId) => {
    setVoiding(true);
    try {
      await api.voidInvoice(targetId, { reason: voidReason });
      show('Invoice voided'); setVoidOpen(false);
      if (view === 'detail') refreshDetail(); else loadItems();
      loadItems();
    } catch (e) { show(e.response?.data?.message || 'Void failed', 'error'); }
    finally { setVoiding(false); }
  };

  const openMarkReceived = (inv) => {
    setMarkTarget({ id: inv.id, invoice_ref: inv.invoice_ref });
    setMarkForm({ payment_date: new Date().toISOString().slice(0, 16), reference_number: '', notes: '' });
    setMarkOpen(true);
  };

  const submitMarkReceived = async () => {
    setMarking(true);
    try {
      const res = await api.markPaymentReceived(markTarget.id, markForm);
      const tokensGranted = res.data?.tokens_granted || 0;
      const msg = tokensGranted > 0
        ? `Payment confirmed — ${Number(tokensGranted).toLocaleString('en-IN')} tokens granted to merchant`
        : 'Payment marked as received';
      show(msg, 'success');
      setMarkOpen(false);
      if (view === 'detail') refreshDetail();
      loadItems();
    } catch (e) { show(e.response?.data?.message || 'Failed', 'error'); }
    finally { setMarking(false); }
  };

  const submitRefund = async () => {
    const amt = Number(refundForm.refund_amount);
    if (!refundForm.refund_amount || amt <= 0) { setRefundErrors({ refund_amount: 'Enter a refund amount greater than 0' }); return; }
    if (invoice && amt > Number(invoice.grand_total)) { setRefundErrors({ refund_amount: `Cannot exceed invoice total (${fmtAmt(invoice.grand_total, invoice.currency_code)})` }); return; }
    setRefundErrors({});
    setRefunding(true);
    try {
      await api.refundInvoice(invoice.id, { ...refundForm, refund_amount: amt });
      show('Refund processed — subscription cancelled');
      setRefundOpen(false); refreshDetail(); loadItems();
    } catch (e) { show(e.response?.data?.message || 'Refund failed', 'error'); }
    finally { setRefunding(false); }
  };

  if (!canView) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="text.secondary">You don't have permission to view invoices.</Typography>
    </Box>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RECORD PAYMENT — standard MUI theme, no custom brand colors
  // ══════════════════════════════════════════════════════════════════════════
  if (view === 'record') {
    const active      = subs.filter(s => s.status === 'active');
    const selSub      = active.find(s => s.id === Number(payForm.subscription_id));
    const planV       = selSub ? planVersions.find(pv => pv.plan_version_id === selSub.plan_version_id) : null;
    const allPricing  = planV?.pricing || [];
    // Block if subscription already has a payment — admin should use "Mark as Received" instead
    const hasPayment  = !!(selSub?.payment_id);
    const hasPending  = hasPayment && (items.some(inv =>
      inv.subscription_id === selSub?.id && inv.status === 'issued'));
    const currencies = [...new Set(allPricing.map(p => p.currency_code))];
    const periods    = allPricing.filter(p => p.currency_code === payForm.currency_code).map(p => p.billing_period);

    return (
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center"
          sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper',
            borderBottom: '1px solid', borderColor: 'divider',
            py: 1.5, mb: 3, mx: -3, px: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Tooltip title="Back to invoices">
              <IconButton size="small" onClick={() => setView('list')}
                sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.100' } }}>
                <ArrowBack fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography variant="h6" fontWeight={800} letterSpacing={-0.3} lineHeight={1.2}>Record Payment</Typography>
              <Typography variant="caption" color="text.secondary">
                Amount auto-filled from plan · invoice issued on save
              </Typography>
            </Box>
          </Box>
          <Tooltip title={hasPayment ? 'This subscription already has a payment — use Mark as Received instead' : ''}>
            <span>
              <Button variant="contained"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
                onClick={submitPayment} disabled={saving || hasPayment}
                sx={{ borderRadius: 2, fontWeight: 700, minWidth: 190,
                  boxShadow: hasPayment ? 'none' : '0 2px 8px rgba(0,0,0,0.15)' }}>
                {saving ? 'Processing…' : 'Record & Issue Invoice'}
              </Button>
            </span>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Subscription */}
          <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Typography fontWeight={700}>Subscription</Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              <Autocomplete
                size="small"
                fullWidth
                options={active}
                getOptionLabel={s => `${s.merchant_name || ''} ${s.merchant_email || ''} ${s.merchant_mobile || ''} ${s.merchant_whatsapp || ''} ${s.merchant_company || ''} ${s.plan_name || ''}`}
                filterOptions={(opts, { inputValue }) => {
                  const q = inputValue.toLowerCase();
                  return opts.filter(s =>
                    (s.merchant_name || '').toLowerCase().includes(q) ||
                    (s.merchant_email || '').toLowerCase().includes(q) ||
                    (s.merchant_mobile || '').toLowerCase().includes(q) ||
                    (s.merchant_whatsapp || '').toLowerCase().includes(q) ||
                    (s.merchant_company || '').toLowerCase().includes(q) ||
                    (s.plan_name || '').toLowerCase().includes(q)
                  );
                }}
                value={active.find(s => s.id === Number(payForm.subscription_id)) || null}
                onChange={(_, opt) => {
                  handleSubSelect(opt ? opt.id : '');
                  if (payErrors.subscription_id) setPayErrors(p => ({ ...p, subscription_id: '' }));
                }}
                renderOption={(props, s) => (
                  <li {...props} key={s.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{s.merchant_name}</Typography>
                      <Typography variant="caption" color="text.disabled">{s.plan_name}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Select active subscription *"
                    error={!!payErrors.subscription_id}
                    helperText={payErrors.subscription_id || ''}
                    placeholder="Search by name, email, mobile, WhatsApp or company…" />
                )}
              />
              {selSub && (
                <Box sx={{ mt: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                  {/* Header row */}
                  <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography fontWeight={800} fontSize={15}>{selSub.merchant_name}</Typography>
                      {selSub.merchant_company && (
                        <Typography variant="caption" color="text.secondary">{selSub.merchant_company}</Typography>
                      )}
                    </Box>
                    <Box display="flex" gap={1} alignItems="center">
                      <Chip size="small" label={selSub.plan_name || '—'}
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, '& .MuiChip-label': { px: 1.2 } }} />
                      <Chip size="small" label={selSub.status}
                        color={selSub.status === 'active' ? 'success' : selSub.status === 'grace' ? 'warning' : 'default'}
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, textTransform: 'capitalize', '& .MuiChip-label': { px: 1.2 } }} />
                    </Box>
                  </Box>
                  {/* Detail grid */}
                  <Box sx={{ px: 2.5, py: 2, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                    <LabelValue label="Email"    value={selSub.merchant_email || '—'} />
                    <LabelValue label="Mobile"   value={selSub.merchant_mobile   || '—'} />
                    <LabelValue label="WhatsApp" value={selSub.merchant_whatsapp || '—'} />
                    {(selSub.merchant_city || selSub.merchant_state) && (
                      <LabelValue label="Location"
                        value={[selSub.merchant_city, selSub.merchant_state].filter(Boolean).join(', ')} />
                    )}
                    {selSub.merchant_address && (
                      <LabelValue label="Address" value={selSub.merchant_address} />
                    )}
                    <LabelValue label="Expiry"
                      value={selSub.expiry_date ? new Date(selSub.expiry_date).toLocaleDateString('en-IN') : '—'} />
                  </Box>
                </Box>
              )}
              {hasPending && (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: 2, fontSize: 13 }}>
                  This subscription already has a <strong>pending payment</strong>.
                  Go to <strong>Invoices → Mark as Received</strong> to confirm it instead of creating a duplicate.
                </Alert>
              )}
              {hasPayment && !hasPending && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2, fontSize: 13 }}>
                  This subscription already has a payment recorded.
                  Creating another payment will generate a duplicate invoice.
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Pricing & Amount */}
          <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontWeight={700}>Pricing & Amount</Typography>
                {gst && (
                  <Chip size="small" label={fmtAmt(gst.grand, payForm.currency_code)}
                    color="success"
                    sx={{ fontWeight: 800, fontSize: 12, height: 22, '& .MuiChip-label': { px: 1.2 } }} />
                )}
              </Box>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              {!selSub ? (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.disabled">
                    Select a subscription above to see pricing.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                  {/* Currency chips */}
                  {currencies.length > 0 && (
                    <Box>
                      <Typography variant="caption" fontWeight={700} color="text.secondary"
                        letterSpacing={0.5} textTransform="uppercase" display="block" mb={1}>
                        Currency
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {currencies.map(c => (
                          <Box key={c}
                            onClick={() => handleCurrencySelect(c)}
                            sx={{
                              px: 2, py: 0.75, borderRadius: 2, cursor: 'pointer',
                              border: '1px solid',
                              borderColor: payForm.currency_code === c ? 'primary.main' : 'divider',
                              bgcolor: payForm.currency_code === c ? 'primary.50' : 'background.paper',
                              color: payForm.currency_code === c ? 'primary.dark' : 'text.secondary',
                              fontWeight: 700, fontSize: 13,
                              transition: 'all 0.15s',
                              '&:hover': { borderColor: 'primary.300', bgcolor: 'primary.50' },
                            }}>
                            {c}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Billing period chips */}
                  {payForm.currency_code && periods.length > 0 && (
                    <Box>
                      <Typography variant="caption" fontWeight={700} color="text.secondary"
                        letterSpacing={0.5} textTransform="uppercase" display="block" mb={1}>
                        Billing Period
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {periods.map(p => {
                          const row = allPricing.find(r => r.billing_period === p && r.currency_code === payForm.currency_code);
                          const price = row?.mrp_incl_tax;
                          return (
                            <Box key={p}
                              onClick={() => handlePeriodSelect(p, allPricing)}
                              sx={{
                                px: 2, py: 1, borderRadius: 2, cursor: 'pointer',
                                border: '1px solid',
                                borderColor: payForm.billing_period === p ? 'primary.main' : 'divider',
                                bgcolor: payForm.billing_period === p ? 'primary.50' : 'background.paper',
                                transition: 'all 0.15s',
                                '&:hover': { borderColor: 'primary.300', bgcolor: 'primary.50' },
                                minWidth: 110,
                              }}>
                              <Typography fontWeight={700} fontSize={13}
                                color={payForm.billing_period === p ? 'primary.dark' : 'text.primary'}
                                sx={{ textTransform: 'capitalize' }}>
                                {p.replace(/_/g, ' ')}
                              </Typography>
                              {price && (
                                <Typography variant="caption" fontWeight={600}
                                  color={payForm.billing_period === p ? 'primary.main' : 'text.secondary'}>
                                  {fmtAmt(price, payForm.currency_code)}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* No pricing configured — manual amount input */}
                  {currencies.length === 0 && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 2 }}>
                      <TextField label="Amount *" type="number" size="small" fullWidth
                        value={payForm.amount}
                        error={!!payErrors.amount}
                        helperText={payErrors.amount || ' '}
                        onChange={e => { setPayForm(p => ({ ...p, amount: e.target.value })); if (payErrors.amount) setPayErrors(p => ({ ...p, amount: '' })); }}
                        inputProps={{ min: 0, step: 0.01 }} />
                      {masterCurrencies.length > 0 ? (
                        <FormControl size="small" sx={{ minWidth: 110 }} error={!!payErrors.currency_code}>
                          <InputLabel>Currency *</InputLabel>
                          <Select label="Currency *" value={payForm.currency_code}
                            onChange={e => { setPayForm(p => ({ ...p, currency_code: e.target.value })); if (payErrors.currency_code) setPayErrors(p => ({ ...p, currency_code: '' })); }}>
                            {masterCurrencies.map(c => (
                              <MenuItem key={c.code} value={c.code}>{c.code}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{payErrors.currency_code || ' '}</FormHelperText>
                        </FormControl>
                      ) : (
                        <TextField label="Currency *" size="small" sx={{ width: 110 }}
                          value={payForm.currency_code}
                          error={!!payErrors.currency_code}
                          helperText={payErrors.currency_code || ' '}
                          onChange={e => { setPayForm(p => ({ ...p, currency_code: e.target.value.toUpperCase().slice(0, 3) })); if (payErrors.currency_code) setPayErrors(p => ({ ...p, currency_code: '' })); }}
                          inputProps={{ maxLength: 3 }} />
                      )}
                    </Box>
                  )}

                  {/* Coupon — shown after billing period is selected */}
                  {payForm.billing_period && (
                    <Box>
                      <Typography variant="caption" fontWeight={700} color="text.secondary"
                        letterSpacing={0.5} textTransform="uppercase" display="block" mb={1}>
                        Coupon <Typography component="span" variant="caption" color="text.disabled"
                          fontWeight={400} textTransform="none">(optional)</Typography>
                      </Typography>

                      {couponPreview ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200',
                          borderRadius: 2, px: 2, py: 1.5 }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <CheckCircleOutlined sx={{ color: 'success.main', fontSize: 18 }} />
                            <Box>
                              <Typography fontWeight={700} fontSize={13} color="success.dark">
                                {couponPreview.code}
                                {couponPreview.description && (
                                  <Typography component="span" fontWeight={400} fontSize={12}
                                    color="text.secondary" ml={1}>— {couponPreview.description}</Typography>
                                )}
                              </Typography>
                              {couponPreview.discount_amount != null && (
                                <Box display="flex" alignItems="center" gap={1} mt={0.25} flexWrap="wrap">
                                  <Typography variant="caption" color="text.secondary"
                                    sx={{ textDecoration: 'line-through' }}>
                                    {fmtAmt(couponPreview.original_price, couponPreview.currency_code)}
                                  </Typography>
                                  <Typography variant="caption" color="error.main" fontWeight={700}>
                                    −{fmtAmt(couponPreview.discount_amount, couponPreview.currency_code)}
                                  </Typography>
                                  <Typography variant="body2" fontWeight={800} color="success.dark">
                                    = {fmtAmt(couponPreview.discounted_price, couponPreview.currency_code)}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                          <Tooltip title="Remove coupon">
                            <IconButton size="small" onClick={removeCoupon}
                              sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box display="flex" gap={1.5} alignItems="flex-start">
                          <TextField size="small" sx={{ flex: 1 }}
                            label="Coupon Code"
                            placeholder="e.g. LAUNCH50"
                            value={couponInput}
                            onChange={e => { setCouponInput(e.target.value.toUpperCase().replace(/\s/g, '')); setCouponError(''); }}
                            onKeyDown={e => { if (e.key === 'Enter') applyCoupon(); }}
                            error={!!couponError}
                            helperText={couponError || ''}
                            inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 } }}
                            InputProps={{ startAdornment: (
                              <Box component="span" sx={{ mr: 1, display: 'flex' }}>
                                <WarningAmberOutlined sx={{ fontSize: 15, color: 'text.disabled' }} />
                              </Box>
                            )}} />
                          <Button variant="outlined" size="medium" onClick={applyCoupon}
                            disabled={!couponInput.trim() || couponApplying}
                            startIcon={couponApplying ? <CircularProgress size={13} /> : null}
                            sx={{ whiteSpace: 'nowrap', minWidth: 90, mt: 0.05 }}>
                            {couponApplying ? 'Checking…' : 'Apply'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Amount field — shown after period selected or no pricing */}
                  {(payForm.billing_period || currencies.length === 0) && (
                    <Box>
                      <Typography variant="caption" fontWeight={700} color="text.secondary"
                        letterSpacing={0.5} textTransform="uppercase" display="block" mb={1}>
                        {couponPreview ? 'Discounted Amount' : gst ? 'Amount (auto-filled)' : 'Amount'}
                      </Typography>
                      <TextField fullWidth size="small"
                        label={`Amount payable (${payForm.currency_code || 'INR'})`}
                        type="number"
                        value={payForm.amount}
                        onChange={e => { setPayForm(p => ({ ...p, amount: e.target.value })); setGst(null); removeCoupon(); }}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                          sx: { fontWeight: 700, fontSize: 16 },
                          startAdornment: (
                            <Box component="span" sx={{ mr: 1, fontWeight: 700,
                              color: 'text.secondary', fontSize: 14 }}>
                              {payForm.currency_code || '₹'}
                            </Box>
                          ),
                        }}
                        helperText={gst
                          ? `Taxable: ${fmtAmt(gst.taxable, payForm.currency_code)} + Tax: ${fmtAmt(gst.total_tax, payForm.currency_code)}`
                          : 'Enter amount or select billing period above to auto-fill'}
                        sx={{ ...NUM_NO_SPIN_INV, '& .MuiInputBase-input': { fontWeight: 700, fontSize: 18 } }} />
                    </Box>
                  )}

                  {/* GST breakdown */}
                  {gst && gst.total_tax > 0 && (
                    <Box sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider',
                      borderRadius: 2, overflow: 'hidden' }}>
                      <Box sx={{ px: 2, py: 1, bgcolor: 'success.50',
                        borderBottom: '1px solid', borderColor: 'success.200',
                        display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleOutlined sx={{ fontSize: 14, color: 'success.dark' }} />
                        <Typography fontWeight={700} fontSize={11} color="success.dark"
                          letterSpacing={0.6} textTransform="uppercase">
                          GST Breakdown
                        </Typography>
                      </Box>
                      <Box sx={{ px: 2.5, py: 2 }}>
                        {[
                          ['Taxable Amount', gst.taxable],
                          ...(gst.cgst     > 0 ? [['CGST',       gst.cgst]]      : []),
                          ...(gst.sgst     > 0 ? [['SGST',       gst.sgst]]      : []),
                          ...(gst.igst     > 0 ? [['IGST',       gst.igst]]      : []),
                          ...(gst.cess     > 0 ? [['CESS',       gst.cess]]      : []),
                          ...(gst.total_tax > 0 ? [['Total Tax', gst.total_tax]] : []),
                        ].map(([label, val]) => (
                          <Box key={label} display="flex" justifyContent="space-between" mb={0.75}>
                            <Typography variant="body2" color="text.secondary">{label}</Typography>
                            <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                              {fmtAmt(val, payForm.currency_code)}
                            </Typography>
                          </Box>
                        ))}
                        <Divider sx={{ my: 1.5 }} />
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography fontWeight={800} fontSize={14}>Total Payable</Typography>
                          <Typography fontWeight={800} fontSize={18} color="primary.main"
                            fontFamily="'Roboto Mono', monospace">
                            {fmtAmt(gst.grand, payForm.currency_code)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Payment Details */}
          <Accordion defaultExpanded disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Typography fontWeight={700}>Payment Details</Typography>
                {payForm.gateway && payForm.gateway !== 'manual' && (
                  <Chip size="small" label={payForm.gateway}
                    sx={{ fontWeight: 700, fontSize: 11, height: 20, textTransform: 'capitalize',
                      '& .MuiChip-label': { px: 1 } }} />
                )}
                {payForm.status === 'pending' && (
                  <Chip size="small" label="Pending" color="warning"
                    sx={{ fontWeight: 700, fontSize: 11, height: 20, '& .MuiChip-label': { px: 1 } }} />
                )}
              </Box>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <FormControl size="small">
                  <InputLabel>Gateway</InputLabel>
                  <Select label="Gateway" value={payForm.gateway}
                    onChange={e => setPayForm(p => ({ ...p, gateway: e.target.value }))}>
                    {['manual', 'offline', 'razorpay', 'stripe'].map(g => (
                      <MenuItem key={g} value={g} sx={{ textTransform: 'capitalize' }}>{g}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText> </FormHelperText>
                </FormControl>
                <FormControl size="small">
                  <InputLabel>Payment Status</InputLabel>
                  <Select label="Payment Status" value={payForm.status}
                    onChange={e => setPayForm(p => ({ ...p, status: e.target.value }))}>
                    <MenuItem value="captured">Captured</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                  </Select>
                  <FormHelperText> </FormHelperText>
                </FormControl>
                <FormControl size="small">
                  <InputLabel>Payment Mode</InputLabel>
                  <Select label="Payment Mode" value={payForm.payment_mode}
                    onChange={e => setPayForm(p => ({ ...p, payment_mode: e.target.value }))}>
                    <MenuItem value="">— None —</MenuItem>
                    {[
                      { value: 'credit_card', label: 'Credit Card' },
                      { value: 'debit_card',  label: 'Debit Card'  },
                      { value: 'cash',        label: 'Cash'        },
                      { value: 'cheque',      label: 'Cheque'      },
                      { value: 'netbanking',  label: 'Net Banking' },
                      { value: 'upi',         label: 'UPI'         },
                    ].map(({ value, label }) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Optional</FormHelperText>
                </FormControl>
                <TextField label="Payment Date" size="small" type="date"
                  value={payForm.payment_date}
                  onChange={e => setPayForm(p => ({ ...p, payment_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                  helperText="Optional — defaults to today" />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Notes */}
          <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1.5, minHeight: 52 }}>
              <Typography fontWeight={700}>Admin Notes</Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ p: 3 }}>
              <TextField label="Notes (optional)" size="small" fullWidth multiline rows={3}
                value={payForm.admin_notes}
                onChange={e => setPayForm(p => ({ ...p, admin_notes: e.target.value }))}
                placeholder="Internal notes about this payment…" />
            </AccordionDetails>
          </Accordion>
        </Box>

        <Snackbar open={snack.open} autoHideDuration={4000}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DETAIL — clean, elegant, print-ready invoice
  // ══════════════════════════════════════════════════════════════════════════
  if (view === 'detail') {
    const inv = invoice;
    const cur = inv?.currency_code || 'INR';
    const showGst = cur === 'INR';

    return (
      <Box sx={{ p: 3, bgcolor: '#F7F8FA', minHeight: '100vh',
        '@media print': { bgcolor: 'white', p: 0 } }}>

        {/* Action bar — hidden on print */}
        <Box display="flex" justifyContent="space-between" alignItems="center"
          sx={{ position: 'sticky', top: 0, zIndex: 10,
            bgcolor: 'rgba(247,248,250,0.96)', backdropFilter: 'blur(8px)',
            borderBottom: '1px solid', borderColor: 'divider',
            py: 1.5, mb: 3, mx: -3, px: 3,
            '@media print': { display: 'none' } }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Tooltip title="Back to invoices">
              <IconButton size="small" onClick={() => setView('list')}
                sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.100' } }}>
                <ArrowBack fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6" fontWeight={800} letterSpacing={-0.3} lineHeight={1.2}>
                  {inv ? inv.invoice_ref : 'Invoice'}
                </Typography>
                {inv && <StatusChip status={inv.status}  />}
              </Box>
              {inv && (
                <Typography variant="caption" color="text.secondary">
                  {fmt(inv.issue_date)} · {inv.merchant_name}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            {/* ── Payment link actions (only when issued) ── */}
            {inv && inv.status === 'issued' && (
              <Button variant="outlined" size="small"
                startIcon={linkSending ? <CircularProgress size={13} /> : <ShareIcon sx={{ fontSize: 15 }} />}
                onClick={handleOpenLinkDialog}
                disabled={linkSending}
                sx={{ borderRadius: '8px', fontWeight: 600, fontSize: 12,
                      borderColor: '#6318C6', color: '#6318C6', textTransform: 'none',
                      '&:hover': { bgcolor: '#f3eeff', borderColor: '#6318C6' } }}>
                {linkSending ? 'Sending…' : inv.payment_link_id ? 'Resend Link' : 'Send Payment Link'}
              </Button>
            )}
            {inv && inv.status === 'issued' && inv.payment_link_id && (
              <Button variant="outlined" size="small"
                startIcon={syncingPayment ? <CircularProgress size={13} /> : <Refresh sx={{ fontSize: 15 }} />}
                onClick={handleSyncPaymentStatus}
                disabled={syncingPayment}
                sx={{ borderRadius: '8px', fontWeight: 600, fontSize: 12, textTransform: 'none' }}>
                {syncingPayment ? 'Checking…' : 'Sync Status'}
              </Button>
            )}
            {inv && inv.status === 'issued' && inv.payment_link_id && (
              <Button variant="outlined" size="small" color="warning"
                startIcon={cancellingLink ? <CircularProgress size={13} /> : <CancelLinkIcon sx={{ fontSize: 15 }} />}
                onClick={() => setCancelLinkOpen(true)}
                disabled={cancellingLink}
                sx={{ borderRadius: '8px', fontWeight: 600, fontSize: 12, textTransform: 'none' }}>
                {cancellingLink ? 'Cancelling…' : 'Cancel Link'}
              </Button>
            )}

            {/* ── Print (always visible) ── */}
            {inv && (
              <Button variant="outlined" size="small" startIcon={<Print sx={{ fontSize: 15 }} />}
                onClick={handlePrint}
                sx={{ borderRadius: '8px', fontWeight: 600, fontSize: 12, textTransform: 'none' }}>
                Print
              </Button>
            )}

            {/* ── Primary: Mark as Received ── */}
            {inv && inv.status === 'issued' && canIssue && (
              <Button variant="contained" color="success" size="small"
                startIcon={<TaskAlt sx={{ fontSize: 15 }} />}
                onClick={() => openMarkReceived(inv)}
                sx={{ borderRadius: '8px', fontWeight: 700, fontSize: 12, textTransform: 'none',
                      boxShadow: '0 1px 4px rgba(34,197,94,0.35)' }}>
                Mark as Received
              </Button>
            )}

            {/* ── Destructive: Void / Refund ── */}
            {inv && ['issued','paid'].includes(inv.status) && canVoid && (
              <Button variant="outlined" color="error" size="small"
                startIcon={<VoidIcon sx={{ fontSize: 15 }} />}
                onClick={() => { setVoidReason(''); setVoidOpen(true); }}
                sx={{ borderRadius: '8px', fontWeight: 600, fontSize: 12, textTransform: 'none' }}>
                Void
              </Button>
            )}
            {inv && inv.status === 'paid' && canRefund && (
              <Button variant="contained" size="small" color="warning"
                startIcon={<RefundIcon sx={{ fontSize: 15 }} />}
                onClick={() => setRefundOpen(r => !r)}
                sx={{ borderRadius: '8px', fontWeight: 700, fontSize: 12, textTransform: 'none',
                      boxShadow: '0 1px 4px rgba(237,137,54,0.35)' }}>
                {refundOpen ? 'Close Refund' : 'Process Refund'}
              </Button>
            )}
          </Box>
        </Box>

        {/* Payment link result banner */}
        {linkResult && (
          <Box sx={{ maxWidth: 860, mx: 'auto', mb: 2 }}>
            <Alert
              severity={linkResult.sent ? 'success' : 'warning'}
              onClose={() => setLinkResult(null)}
              icon={<LinkIcon />}
              sx={{ borderRadius: 2 }}
            >
              <strong>{linkResult.sent ? `Link sent to ${linkResult.email}` : 'Email delivery failed — link generated'}</strong>
              <Box mt={0.75} sx={{ wordBreak: 'break-all', fontSize: 12, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1, px: 1.5, py: 1, fontFamily: 'monospace' }}>
                {linkResult.payment_url}
              </Box>
              <Button size="small" sx={{ mt: 1, fontWeight: 600 }}
                onClick={() => { navigator.clipboard.writeText(linkResult.payment_url); show('Link copied!', 'success'); }}>
                Copy Link
              </Button>
            </Alert>
          </Box>
        )}

        {/* Disputed payment warning */}
        {inv && inv.status === 'issued' && inv.payment_status === 'voided' && (
          <Box sx={{ maxWidth: 860, mx: 'auto', mb: 2 }}>
            <Alert severity="error" icon={<WarningAmberOutlined />} sx={{ borderRadius: 2 }}>
              <strong>Payment under dispute.</strong> A chargeback has been filed for this payment.
              The payment has been frozen. Respond to the dispute in your Razorpay dashboard.
            </Alert>
          </Box>
        )}

        {/* Payment link expired / cancelled warning */}
        {inv && inv.status === 'issued' && !inv.payment_link_id && inv.payment_link_last_sent_at && !linkResult && (
          <Box sx={{ maxWidth: 860, mx: 'auto', mb: 2 }}>
            <Alert severity="warning" icon={<LinkIcon />} sx={{ borderRadius: 2 }}>
              <strong>Payment link expired or cancelled.</strong> The previous link sent on{' '}
              {new Date(inv.payment_link_last_sent_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}{' '}
              is no longer valid. Click <strong>Share Payment Link</strong> to send a new one.
            </Alert>
          </Box>
        )}

        {detailLoading ? (
          <Box sx={{ maxWidth: 860, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[160, 200, 100, 280, 140].map((h, i) => (
              <Skeleton key={i} height={h} sx={{ borderRadius: 3 }} variant="rectangular" />
            ))}
          </Box>
        ) : inv ? (
          <Box sx={{ maxWidth: 860, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Refund panel */}
            {refundOpen && (
              <Paper elevation={0}
                sx={{ borderRadius: 3, border: '1px solid #FFCC80', bgcolor: '#FFFDE7', overflow: 'hidden' }}>
                <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  borderBottom: '1px solid #FFE082' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <WarningAmberOutlined sx={{ color: '#F57C00', fontSize: 18 }} />
                    <Typography fontWeight={800} fontSize={14} color="#E65100">Process Refund</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setRefundOpen(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>
                    This marks the invoice as <strong>refunded</strong> and automatically cancels the merchant's subscription.
                  </Alert>
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={3}>
                      <TextField label="Refund amount *" type="number" size="small" fullWidth
                        value={refundForm.refund_amount}
                        error={!!refundErrors.refund_amount}
                        helperText={refundErrors.refund_amount || `Max: ${fmtAmt(inv.grand_total, cur)}`}
                        onChange={e => { setRefundForm(f => ({ ...f, refund_amount: e.target.value })); if (refundErrors.refund_amount) setRefundErrors({}); }}
                        inputProps={{ min: 0, max: inv.grand_total, step: 0.01 }} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField label="Reference / UTR" size="small" fullWidth
                        value={refundForm.refund_reference}
                        onChange={e => setRefundForm(f => ({ ...f, refund_reference: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Reason" size="small" fullWidth
                        value={refundForm.reason}
                        onChange={e => setRefundForm(f => ({ ...f, reason: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button variant="contained" color="warning" fullWidth onClick={submitRefund}
                        disabled={refunding} sx={{ borderRadius: 2, fontWeight: 700, height: 40 }}>
                        {refunding ? <CircularProgress size={16} color="inherit" /> : 'Confirm'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            )}

            {/* ── INVOICE DOCUMENT ─────────────────────────────────────── */}
            <Paper elevation={0} id="invoice-print-area"
              sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: 'white',
                boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
                border: '1.5px solid #D1D5DB',
                '@media print': { boxShadow: 'none', border: 'none', borderRadius: 0 } }}>

              {/* Brand accent line — only color in the whole document */}
              <Box sx={{ height: 3, background: INV.gradient }} />

              {/* ── HEADER: Logo · Invoice info ── */}
              <Box sx={{ px: 5, pt: 4, pb: 3,
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Box component="img" src="/assets/img/glammify-logo.png" alt="Glammify"
                    sx={{ height: 42, objectFit: 'contain', display: 'block' }} />
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#4B5563',
                    letterSpacing: 2, textTransform: 'uppercase', mb: 1 }}>
                    {inv.invoice_type?.replace(/_/g, ' ') || 'Tax Invoice'}
                  </Typography>
                  <Typography sx={{ fontSize: 30, fontWeight: 800, color: '#111827',
                    fontFamily: "'Roboto Mono', monospace", letterSpacing: -0.5, lineHeight: 1 }}>
                    {inv.invoice_ref}
                  </Typography>
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <StatusChip status={inv.status}  />
                  </Box>
                  <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'flex-end' }}>
                    {[
                      ['Issue Date', fmt(inv.issue_date)],
                      ...(inv.due_date ? [['Due Date', fmt(inv.due_date)]] : []),
                    ].map(([lbl, val]) => (
                      <Box key={lbl} display="flex" gap={1.5} alignItems="baseline">
                        <Typography sx={{ fontSize: 12.5, color: '#4B5563', fontWeight: 500 }}>{lbl}</Typography>
                        <Typography sx={{ fontSize: 13, color: '#111827', fontWeight: 700,
                          fontFamily: "'Roboto Mono', monospace" }}>{val}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ mx: 5, borderColor: '#E5E7EB' }} />

              {/* ── BILLED BY / BILLED TO ── */}
              <Box sx={{ px: 5, py: 3.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                <Box>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1.2,
                    textTransform: 'uppercase', color: '#4B5563', mb: 1.2 }}>
                    From
                  </Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111827', mb: 0.5 }}>
                    Glammify AI
                  </Typography>
                  {inv.seller_gstin && (
                    <Typography sx={{ fontSize: 13, color: '#374151' }}>
                      GSTIN: <Box component="span" sx={{ fontFamily: "'Roboto Mono', monospace",
                        fontWeight: 700, color: '#111827' }}>{inv.seller_gstin}</Box>
                    </Typography>
                  )}
                  {inv.seller_hsn_sac && (
                    <Typography sx={{ fontSize: 13, color: '#374151', mt: 0.2 }}>
                      HSN/SAC: {inv.seller_hsn_sac}
                    </Typography>
                  )}
                  {inv.seller_address && (
                    <Typography sx={{ fontSize: 13, color: '#4B5563', mt: 0.5, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                      {inv.seller_address}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1.2,
                    textTransform: 'uppercase', color: '#4B5563', mb: 1.2 }}>
                    To
                  </Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111827', mb: 0.5 }}>
                    {inv.buyer_legal_name}
                  </Typography>
                  {inv.buyer_contact_name && inv.buyer_contact_name !== inv.buyer_legal_name && (
                    <Typography sx={{ fontSize: 13, color: '#374151' }}>
                      Attn: {inv.buyer_contact_name}
                    </Typography>
                  )}
                  {inv.buyer_gstin ? (
                    <Typography sx={{ fontSize: 13, color: '#374151' }}>
                      GSTIN: <Box component="span" sx={{ fontFamily: "'Roboto Mono', monospace",
                        fontWeight: 700, color: '#111827' }}>{inv.buyer_gstin}</Box>
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: 13, color: '#4B5563', fontStyle: 'italic' }}>
                      NA
                    </Typography>
                  )}
                  {inv.buyer_address && (
                    <Typography sx={{ fontSize: 13, color: '#4B5563', mt: 0.5,
                      lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                      {inv.buyer_address}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider sx={{ mx: 5, borderColor: '#E5E7EB' }} />

              {/* ── LINE ITEMS ── */}
              <Box sx={{ px: 5, py: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        ['#', { width: 32 }],
                        ['Description', {}],
                        ['HSN/SAC', { width: 88 }],
                        ['Qty', { width: 44, align: 'center' }],
                        ['Unit Price', { width: 120, align: 'right' }],
                        ['Taxable', { width: 120, align: 'right' }],
                        ...(showGst ? [
                          ['CGST%', { width: 64, align: 'center' }],
                          ['SGST%', { width: 64, align: 'center' }],
                          ['IGST%', { width: 64, align: 'center' }],
                        ] : []),
                        ['Tax', { width: 100, align: 'right' }],
                        ['Total', { width: 120, align: 'right' }],
                      ].map(([lbl, opts]) => (
                        <TableCell key={lbl} width={opts.width} align={opts.align || 'left'}
                          sx={{ fontSize: 11.5, fontWeight: 700, color: '#374151',
                            letterSpacing: 0.6, textTransform: 'uppercase',
                            borderBottom: '2px solid #111827', pb: 1.2, pt: 0.5, px: 1 }}>
                          {lbl}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(inv.line_items || []).map((li, idx) => {
                      const isDiscount = (li.line_total < 0) || (li.description || '').toLowerCase().includes('discount');
                      const rowColor   = isDiscount ? '#dc2626' : '#111827';
                      const rowBg      = isDiscount ? '#fff5f5' : 'transparent';
                      return (
                        <TableRow key={li.id || idx} sx={{ bgcolor: rowBg }}>
                          <TableCell sx={{ fontSize: 13, color: '#4B5563', py: 1.8, px: 1 }}>
                            {isDiscount ? '—' : idx + 1}
                          </TableCell>
                          <TableCell sx={{ py: 1.8, px: 1 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: isDiscount ? 600 : 600, color: rowColor, fontStyle: isDiscount ? 'italic' : 'normal' }}>
                              {li.description}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, color: '#374151', fontFamily: "'Roboto Mono', monospace", py: 1.8, px: 1 }}>
                            {li.hsn_sac || '—'}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 13, color: '#111827', fontWeight: 600, py: 1.8, px: 1 }}>
                            {li.quantity}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 13.5, color: rowColor, py: 1.8, px: 1, fontFamily: "'Roboto Mono', monospace" }}>
                            {fmtAmt(li.unit_price, cur)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 13.5, color: rowColor, py: 1.8, px: 1, fontFamily: "'Roboto Mono', monospace" }}>
                            {fmtAmt(li.taxable_amount, cur)}
                          </TableCell>
                          {showGst && <>
                            <TableCell align="center" sx={{ fontSize: 13, py: 1.8, px: 1, color: li.cgst_rate > 0 ? '#111827' : '#9CA3AF', fontWeight: li.cgst_rate > 0 ? 700 : 400 }}>
                              {li.cgst_rate > 0 ? `${li.cgst_rate}%` : '—'}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: 13, py: 1.8, px: 1, color: li.sgst_rate > 0 ? '#111827' : '#9CA3AF', fontWeight: li.sgst_rate > 0 ? 700 : 400 }}>
                              {li.sgst_rate > 0 ? `${li.sgst_rate}%` : '—'}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: 13, py: 1.8, px: 1, color: li.igst_rate > 0 ? '#111827' : '#9CA3AF', fontWeight: li.igst_rate > 0 ? 700 : 400 }}>
                              {li.igst_rate > 0 ? `${li.igst_rate}%` : '—'}
                            </TableCell>
                          </>}
                          <TableCell align="right" sx={{ fontSize: 13.5, color: '#374151', fontWeight: 600, py: 1.8, px: 1, fontFamily: "'Roboto Mono', monospace" }}>
                            {fmtAmt(li.tax_amount, cur)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 14, fontWeight: 700, color: rowColor, py: 1.8, px: 1, fontFamily: "'Roboto Mono', monospace" }}>
                            {fmtAmt(li.line_total, cur)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>

              <Divider sx={{ mx: 5, borderColor: '#E5E7EB' }} />

              {/* ── NOTES + TAX SUMMARY ── */}
              <Box sx={{ px: 5, py: 3, display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                {/* Notes */}
                <Box sx={{ flex: 1 }}>
                  {inv.notes && (
                    <>
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#4B5563',
                        letterSpacing: 0.6, textTransform: 'uppercase', mb: 1 }}>
                        Notes
                      </Typography>
                      <Typography sx={{ fontSize: 13.5, color: '#374151', lineHeight: 1.8 }}>
                        {inv.notes}
                      </Typography>
                    </>
                  )}
                </Box>

                {/* Tax summary + grand total */}
                <Box sx={{ minWidth: 260 }}>
                  {[
                    ['Taxable Amount', inv.taxable_amount, false],
                    ...(inv.total_cgst > 0 ? [['CGST', inv.total_cgst, false]] : []),
                    ...(inv.total_sgst > 0 ? [['SGST', inv.total_sgst, false]] : []),
                    ...(inv.total_igst > 0 ? [['IGST', inv.total_igst, false]] : []),
                    ...(inv.total_cess > 0 ? [['CESS', inv.total_cess, false]] : []),
                    ['Total Tax', inv.total_tax, false],
                  ].map(([label, val]) => (
                    <Box key={label} display="flex" justifyContent="space-between" mb={0.8}>
                      <Typography sx={{ fontSize: 13.5, color: '#374151', fontWeight: 500 }}>{label}</Typography>
                      <Typography sx={{ fontSize: 13.5, color: '#111827', fontWeight: 600,
                        fontFamily: "'Roboto Mono', monospace" }}>
                        {fmtAmt(val, cur)}
                      </Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1.5, borderColor: '#111827' }} />
                  <Box display="flex" justifyContent="space-between" alignItems="baseline">
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                      Grand Total
                    </Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#111827',
                      fontFamily: "'Roboto Mono', monospace", letterSpacing: -0.5 }}>
                      {fmtAmt(inv.grand_total, cur)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: '#4B5563', textAlign: 'right', mt: 0.5 }}>
                    {inv.currency_code} · inclusive of all taxes
                  </Typography>
                </Box>
              </Box>

              {/* ── FOOTER ── */}
              <Box sx={{ mx: 5, mb: 3, pt: 2, borderTop: '2px solid #E5E7EB',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box component="img" src="/assets/img/glammify-logo.png" alt="Glammify"
                  sx={{ height: 26, objectFit: 'contain', opacity: 0.85 }} />
                <Typography sx={{ fontSize: 12, color: '#4B5563', fontWeight: 600,
                  fontFamily: "'Roboto Mono', monospace" }}>
                  {inv.invoice_ref}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#4B5563' }}>
                  Computer generated · no signature required
                </Typography>
              </Box>
            </Paper>

          </Box>
        ) : null}

        {/* Void dialog */}
        <Dialog open={voidOpen} onClose={() => setVoidOpen(false)} fullWidth maxWidth="xs"
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
          <Box sx={{ px: 3, py: 2, bgcolor: '#FFEBEE', borderBottom: '1px solid #FFCDD2',
            display: 'flex', alignItems: 'center', gap: 1 }}>
            <VoidIcon sx={{ color: '#C62828', fontSize: 18 }} />
            <Typography fontWeight={800} color="#C62828">Void Invoice</Typography>
          </Box>
          <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="error" sx={{ borderRadius: 2, fontSize: 13 }}>
              Void <strong>{inv?.invoice_ref}</strong>? This cannot be undone.
            </Alert>
            <TextField label="Reason (optional)" size="small" multiline rows={2}
              value={voidReason} onChange={e => setVoidReason(e.target.value)} />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button onClick={() => setVoidOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
            <Button variant="contained" color="error" onClick={() => submitVoid(inv.id)} disabled={voiding}
              sx={{ borderRadius: 2, fontWeight: 700, minWidth: 130 }}>
              {voiding ? <CircularProgress size={16} color="inherit" /> : 'Confirm Void'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Payment Link confirmation */}
        <Dialog open={cancelLinkOpen} onClose={() => setCancelLinkOpen(false)} fullWidth maxWidth="xs"
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
          <Box sx={{ px: 3, py: 2, bgcolor: '#FFF3E0', borderBottom: '1px solid #FFE0B2',
            display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelLinkIcon sx={{ color: '#E65100', fontSize: 18 }} />
            <Typography fontWeight={800} color="#E65100">Cancel Payment Link</Typography>
          </Box>
          <DialogContent sx={{ pt: 2.5 }}>
            <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 13 }}>
              Cancel the payment link for <strong>{inv?.invoice_ref}</strong>?
              The customer will no longer be able to pay through the existing link.
              You can send a new link afterwards.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button onClick={() => setCancelLinkOpen(false)}
              sx={{ borderRadius: '8px', textTransform: 'none' }}>
              Go Back
            </Button>
            <Button variant="contained" color="warning" onClick={handleCancelPaymentLink}
              sx={{ borderRadius: '8px', fontWeight: 700, textTransform: 'none', minWidth: 140 }}>
              Yes, Cancel Link
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Send Payment Link Dialog ── */}
        <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShareIcon sx={{ color: '#6318C6', fontSize: 18 }} />
            <Typography fontWeight={800} color="#6318C6">
              {invoice?.payment_link_id ? 'Resend Payment Link' : 'Send Payment Link'}
            </Typography>
          </Box>
          <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              The payment link will be emailed to this address. You can change it before sending.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Merchant Email *"
              value={linkEmailInput}
              onChange={e => { setLinkEmailInput(e.target.value); setLinkEmailError(''); }}
              error={!!linkEmailError}
              helperText={linkEmailError || (invoice?.merchant_email
                ? 'Pre-filled from merchant profile'
                : 'No email on merchant profile — enter one manually')}
              placeholder="merchant@example.com"
              type="email"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
            <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSendPaymentLink}
              disabled={linkSending}
              startIcon={linkSending ? <CircularProgress size={14} color="inherit" /> : <ShareIcon />}
              sx={{ borderRadius: 2, fontWeight: 700, bgcolor: '#6318C6',
                '&:hover': { bgcolor: '#4e12a0' } }}>
              {linkSending ? 'Sending…' : 'Send Link'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={4000}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // LIST VIEW — standard MUI theme, no custom brand colors
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start"
        mb={3} pb={2.5}
        sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper',
          borderBottom: '1px solid', borderColor: 'divider',
          mx: -3, px: 3, pt: 2.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={-0.5} gutterBottom>Invoices</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              GST-compliant tax invoices &amp; payments
            </Typography>
            {!listLoading && total > 0 && (
              <>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                <Typography variant="body2" color="text.disabled">
                  {total} invoice{total !== 1 ? 's' : ''}
                </Typography>
              </>
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1.5} alignItems="center" mt={0.5}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadItems}
              sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.50' } }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button size="medium" variant="outlined" startIcon={<FileDownloadOutlined />} onClick={handleExport}
            sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}>
            Export CSV
          </Button>
          {canPay && (
            <Button variant="contained" startIcon={<Add />} onClick={openRecord}
              sx={{ borderRadius: 2, fontWeight: 700,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.2)' } }}>
              Record Payment
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2.5, overflow: 'hidden' }}>
        <Box display="flex" gap={1.5} px={2} py={1.5} alignItems="center" flexWrap="wrap"
          sx={{ bgcolor: 'grey.50' }}>
          <TuneRounded sx={{ fontSize: 18, color: 'text.disabled' }} />
          <TextField size="small" placeholder="Search by name, email, mobile, WhatsApp or company…" value={search}
            onChange={e => setSearch(e.target.value)} sx={{ width: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start">
              <Search sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment>,
              sx: { borderRadius: 2, bgcolor: 'white' } }} />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={e => setStatus(e.target.value)}
              sx={{ borderRadius: 2, bgcolor: 'white' }}>
              <MenuItem value="">All invoices</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="issued">Awaiting Payment</MenuItem>
              <MenuItem value="void">Voided</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Payment</InputLabel>
            <Select label="Payment" value={payStatusFilter} onChange={e => setPayStatus(e.target.value)}
              sx={{ borderRadius: 2, bgcolor: 'white' }}>
              <MenuItem value="">All payments</MenuItem>
              <MenuItem value="pending">Payment Pending</MenuItem>
              <MenuItem value="captured">Payment Received</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
            </Select>
          </FormControl>
          {(search || statusFilter || payStatusFilter) && (
            <Button size="small" onClick={() => { setSearch(''); setStatus(''); setPayStatus(''); }}
              sx={{ color: 'error.main', fontWeight: 600, fontSize: 12,
                '&:hover': { bgcolor: 'error.50' } }}>
              Clear
            </Button>
          )}
          <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
            {listLoading ? 'Loading…' : `${total} invoice${total !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...LIST_TH, pl: 2.5, width: 40 }}>#</TableCell>
                <TableCell sx={{ ...LIST_TH, width: 120 }}>
                  <SortTH label="Invoice" field="invoice_number" sortBy={sortBy} sortDir={sortDir} onSort={col => { setSortBy(col); setSortDir(d => col === sortBy ? (d==='asc'?'desc':'asc') : 'desc'); }} />
                </TableCell>
                <TableCell sx={LIST_TH}>Merchant</TableCell>
                <TableCell sx={{ ...LIST_TH, width: 140, textAlign: 'right' }}>
                  <SortTH label="Amount" field="grand_total" sortBy={sortBy} sortDir={sortDir} onSort={col => { setSortBy(col); setSortDir(d => col === sortBy ? (d==='asc'?'desc':'asc') : 'desc'); }} sx={{ justifyContent: 'flex-end' }} />
                </TableCell>
                <TableCell sx={{ ...LIST_TH, width: 100 }}>
                  <SortTH label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={col => { setSortBy(col); setSortDir(d => col === sortBy ? (d==='asc'?'desc':'asc') : 'desc'); }} />
                </TableCell>
                <TableCell sx={{ ...LIST_TH, width: 110 }}>
                  <SortTH label="Date" field="issue_date" sortBy={sortBy} sortDir={sortDir} onSort={col => { setSortBy(col); setSortDir(d => col === sortBy ? (d==='asc'?'desc':'asc') : 'desc'); }} />
                </TableCell>
                <TableCell sx={{ ...LIST_TH, textAlign: 'right', pr: 2, width: 120 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <TableCell key={j} sx={{ py: 1.5 }}><Skeleton height={20} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : items.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8, border: 0 }}>
                      <ReceiptOutlined sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5, display: 'block', mx: 'auto' }} />
                      <Typography variant="body1" fontWeight={600} color="text.secondary" mb={0.5}>
                        {search || statusFilter ? 'No results' : 'No invoices yet'}
                      </Typography>
                      <Typography variant="body2" color="text.disabled" mb={2}>
                        {search || statusFilter
                          ? 'Try adjusting your search or status filter'
                          : 'Record a payment to generate the first invoice'}
                      </Typography>
                      {!search && !statusFilter && canPay && (
                        <Button variant="outlined" startIcon={<Add />} onClick={openRecord} size="small"
                          sx={{ borderRadius: 2, fontWeight: 600 }}>
                          Record first payment
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )
                : items.map((inv, i) => (
                    <TableRow key={inv.id} hover
                      sx={{ cursor: 'pointer',
                        '&:last-child td': { borderBottom: 'none' } }}
                      onClick={() => openDetail(inv.id)}>
                      <TableCell sx={{ pl: 2.5, color: 'text.disabled', fontWeight: 500, fontSize: 13 }}>
                        {page * perPage + i + 1}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}
                          fontFamily="'Roboto Mono', monospace" fontSize={13}>
                          {inv.invoice_ref}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, maxWidth: 200 }}>
                        <Tooltip title={inv.merchant_name} placement="top" arrow>
                          <Typography variant="body2" fontWeight={600}
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {inv.merchant_name}
                          </Typography>
                        </Tooltip>
                        <Tooltip title={inv.merchant_email || ''} placement="bottom" arrow>
                          <Typography variant="caption" color="text.disabled"
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                            {inv.merchant_email}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight={700} fontFamily="monospace">
                          {fmtAmt(inv.grand_total, inv.currency_code)}
                        </Typography>
                      </TableCell>
                      <TableCell><StatusChip status={inv.status}  /></TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {fmt(inv.issue_date)}
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 2 }} onClick={e => e.stopPropagation()}>
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => openDetail(inv.id)} sx={BTN_PRIMARY}>
                              <Visibility sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {inv.status === 'issued' && canIssue && (
                            <Tooltip title="Mark as Received">
                              <IconButton size="small" onClick={() => openMarkReceived(inv)} sx={BTN_SUCCESS}>
                                <TaskAlt sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {['issued','paid'].includes(inv.status) && canVoid && (
                            <Tooltip title="Void">
                              <IconButton size="small" sx={BTN_ERROR}
                                onClick={() => { setInvoice(inv); setVoidReason(''); setVoidOpen(true); }}>
                                <VoidIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {inv.status === 'paid' && canRefund && (
                            <Tooltip title="Refund">
                              <IconButton size="small" onClick={() => openDetail(inv.id)} sx={BTN_WARNING}>
                                <RefundIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={perPage}
        onRowsPerPageChange={e => { setPerPage(+e.target.value); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 0.5 }}
      />

      {/* Mark as Received dialog */}
      <Dialog open={markOpen} onClose={() => !marking && setMarkOpen(false)} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ px: 3, py: 2, bgcolor: '#E8F5E9', borderBottom: '1px solid #C8E6C9',
          display: 'flex', alignItems: 'center', gap: 1 }}>
          <TaskAlt sx={{ color: '#2E7D32', fontSize: 18 }} />
          <Typography fontWeight={800} color="#2E7D32">Mark Payment as Received</Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="success" sx={{ borderRadius: 2, fontSize: 13 }}>
            Confirm payment received for <strong>{markTarget?.invoice_ref}</strong>.<br />
            Invoice will update to <strong>Paid</strong>. If tokens were withheld at subscription creation, they will be <strong>granted to the merchant now</strong>.
          </Alert>
          <TextField size="small" label="Payment Date & Time" type="datetime-local" fullWidth
            value={markForm.payment_date}
            onChange={e => setMarkForm(f => ({ ...f, payment_date: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="Reference / UTR / Transaction ID" fullWidth
            value={markForm.reference_number}
            placeholder="Optional — for audit trail"
            onChange={e => setMarkForm(f => ({ ...f, reference_number: e.target.value }))} />
          <TextField size="small" label="Notes" fullWidth multiline rows={2}
            value={markForm.notes}
            placeholder="Optional internal note"
            onChange={e => setMarkForm(f => ({ ...f, notes: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button onClick={() => setMarkOpen(false)} disabled={marking}>Cancel</Button>
          <Button variant="contained" color="success" onClick={submitMarkReceived} disabled={marking}
            startIcon={marking ? <CircularProgress size={14} color="inherit" /> : <TaskAlt />}
            sx={{ borderRadius: 2, fontWeight: 700 }}>
            {marking ? 'Confirming…' : 'Confirm Payment Received'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void from list */}
      <Dialog open={voidOpen} onClose={() => setVoidOpen(false)} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ px: 3, py: 2, bgcolor: '#FFEBEE', borderBottom: '1px solid #FFCDD2',
          display: 'flex', alignItems: 'center', gap: 1 }}>
          <VoidIcon sx={{ color: '#C62828', fontSize: 18 }} />
          <Typography fontWeight={800} color="#C62828">Void Invoice</Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="error" sx={{ borderRadius: 2, fontSize: 13 }}>
            Void <strong>{invoice?.invoice_ref}</strong>? This cannot be undone.
          </Alert>
          <TextField label="Reason" size="small" multiline rows={2}
            value={voidReason} onChange={e => setVoidReason(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setVoidOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" color="error"
            onClick={() => submitVoid(invoice?.id)} disabled={voiding}
            sx={{ borderRadius: 2, fontWeight: 700 }}>
            {voiding ? <CircularProgress size={16} color="inherit" /> : 'Confirm Void'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
